import React, { useEffect, useState, useCallback } from "react";
import { dms } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Activity, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || ""; }
};
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  return String(v);
};
const label = (k) => k.replace(/_/g, " ");

function ItemsList({ items }) {
  if (!Array.isArray(items) || !items.length) return <span className="text-slate-400">—</span>;
  return (
    <ul className="list-disc ml-5 space-y-0.5">
      {items.map((it, i) => (
        <li key={i}>
          {it.product} — {it.boxes || 0} box{it.pcs ? ` + ${it.pcs} pcs` : ""}
          {it.box_price ? ` @ ₹${fmtVal(it.box_price)}/box` : ""}
        </li>
      ))}
    </ul>
  );
}

function EffectView({ e }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm space-y-1">
      <div className="font-semibold text-slate-800">{e.action || "Change"}</div>
      {Object.entries(e).filter(([k]) => k !== "action").map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <div className="w-44 shrink-0 capitalize text-slate-500">{label(k)}</div>
          <div className="text-slate-800 min-w-0 break-words">
            {Array.isArray(v) ? <ItemsList items={v} /> : fmtVal(v)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ActivityLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [onlySuccess, setOnlySuccess] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (onlySuccess) params.only_success = true;
      const r = await dms.ownerActivityLog(params);
      setRows(r.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load activity log");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, onlySuccess]);

  useEffect(() => { load(); }, [load]);

  const shown = rows.filter((r) => {
    if (!q.trim()) return true;
    const hay = `${r.as_user_name} ${r.owner_name} ${r.path} ${(r.effects || []).map((e) => e.action).join(" ")}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div className="space-y-4" data-testid="activity-log-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Activity size={22} /> Activity Log</h1>
          <p className="text-sm text-slate-500">A record of the Owner's "Login as" changes and the changes distributors made to their own profiles.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
      </div>

      <Card className="p-3 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
        <div className="flex-1 min-w-[180px]"><Label className="text-xs">Search</Label><Input placeholder="Distributor, action, bill..." value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" checked={onlySuccess} onChange={(e) => setOnlySuccess(e.target.checked)} /> Only successful
        </label>
      </Card>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>When</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Logged in as</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Result</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">{loading ? "Loading..." : "No records found"}</TableCell></TableRow>
            )}
            {shown.map((r) => {
              const isOpen = !!open[r.id];
              const title = (r.effects && r.effects[0] && r.effects[0].action) || `${r.method} ${r.path.replace("/api/dms", "")}`;
              const ok = r.status < 400;
              return (
                <React.Fragment key={r.id}>
                  <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}>
                    <TableCell>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</TableCell>
                    <TableCell className="whitespace-nowrap">{fmtTime(r.at)}</TableCell>
                    <TableCell>{r.owner_name || "—"}</TableCell>
                    <TableCell>{r.as_user_name || "—"} <span className="text-xs text-slate-400">{r.as_role}</span></TableCell>
                    <TableCell className="font-medium">{title}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-xs border ${ok ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-rose-100 text-rose-800 border-rose-200"}`}>
                        {ok ? "Success" : `Failed (${r.status})`}
                      </span>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow>
                      <TableCell colSpan={6} className="bg-slate-50">
                        <div className="space-y-2 py-1">
                          <div className="text-xs text-slate-500">{r.method} {r.path}{r.query ? `?${r.query}` : ""} · IP {r.ip || "—"} · {r.duration_ms} ms</div>
                          {(r.effects || []).length === 0
                            ? <div className="text-sm text-slate-500">Details of this action are not recorded yet (only who, when and which URL).</div>
                            : r.effects.map((e, i) => <EffectView key={i} e={e} />)}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
